// Inhalte des Gesamtzertifikats „Fachkraft für Musik und Sprache im Elementarbereich“
// (Auftrag 3, 03.10.2026). PDF (generateMusikSpracheCertificatePDF.ts), Vorschau in
// StudentCertificates und die Prüfansicht in PublicCertificate lesen alle von hier.
//
// Regeln für alle sichtbaren Texte: keine Gedankenstriche, nie „staatlich“, „anerkannt“,
// „zugelassen“ (Test: src/lib/__tests__/musikSpracheZertifikat.test.ts).

import { format } from "date-fns";
import { de } from "date-fns/locale";
import { KURSE } from "@/config/musikUndSpracheLandingpage";

export const MS_ZERTIFIKAT_TITEL = "Fachkraft für Musik und Sprache im Elementarbereich";

/** Start der Ausbildung. Der Zeitraum beginnt frühestens hier. */
export const MS_AUSBILDUNGSSTART = "2026-10-15";

export const MS_ABSCHLUSS_SATZ = "hat die Ausbildung mit allen vier Kursen und dem Abschlussquiz erfolgreich abgeschlossen.";

export const MS_UMFANG_EINHEIT = "Unterrichtsstunden";

export const MS_DOZENTINNEN = ["Tabitha Thieme", "Kati Breuer", "Nicole Knorr"] as const;

/**
 * Sachlicher Satz je Kurs für die Anlage (Daniel, 03.10.2026, wörtlich). Bewusst nicht der Kacheltext
 * der Landingpage: der spricht mit „du“ an.
 */
const KURS_INHALT: readonly string[] = [
  "Meilensteine der Sprachentwicklung, Auffälligkeiten erkennen, Stottern, Mehrsprachigkeit und die Rolle von Musik.",
  "Musikalische Sprachbildung für Kinder unter drei Jahren in acht Bausteinen: Verse, Lieder und Spielideen für die Krippe.",
  "Musikalische Sprachförderung für Kinder über drei Jahren in acht Bausteinen: Lieder, Sprechverse und Spielideen für die Kita.",
  "Lieder, Verse und Spielideen zu den vier Jahreszeiten für den sprachbildenden Kita-Alltag.",
];

/** Die vier Kurse in Kursreihenfolge; Titel und Dozentin aus der Landingpage. */
export const MS_KURSE = KURSE.map((k, i) => {
  const dozentin = k.dozentin.replace(" & ", " und ");
  return {
    titel: k.titel,
    dozentin,
    /** „Dozentin: …“ bzw. „Dozentinnen: …“ bei zwei Namen */
    dozentinZeile: `${dozentin.includes(" und ") ? "Dozentinnen" : "Dozentin"}: ${dozentin}`,
    inhalt: KURS_INHALT[i] ?? "",
  };
});

/** Unterschriftenblock wie auf den Kurszertifikaten */
export const MS_UNTERSCHRIFT_ZEILEN = ["Diplom-Sozialpädagoge", "Leiter von MusiKonzept"] as const;

/** Die acht Bausteine in der Reihenfolge des Auftrags (nicht der der Landingpage). */
export const MS_BAUSTEINE = [
  "Hören und Sprachmelodie",
  "Rhythmus und Wiederholung",
  "Stimme und Betonung",
  "Bewegung und Sprache",
  "Sprachbildung im Alltag",
  "Bewusste sprachliche Impulse",
  "Emotionale Sicherheit",
  "Abgrenzung zur Therapie",
] as const;

const PRUEF_BASIS = "https://www.musikonzept.de/zertifikat/";

export function istMusikSpracheZertifikat(kursTitel: string): boolean {
  return kursTitel.toLowerCase().includes("fachkraft für musik und sprache");
}

/** Ziel des QR-Codes: die öffentliche Prüfseite. */
export function pruefLink(token: string): string {
  return PRUEF_BASIS + token;
}

/** Gedruckte Kurzform unter dem QR-Code (der vollständige Link steckt im Code). */
export const PRUEF_LINK_ANZEIGE = "musikonzept.de/zertifikat/…";

export function umfangText(stunden: number | null | undefined): string {
  const n = typeof stunden === "number" && stunden > 0 ? stunden : 50;
  return `Umfang: ${n.toLocaleString("de-DE")} ${MS_UMFANG_EINHEIT}`;
}

/**
 * „Zeitraum: Oktober 2026 bis Dezember 2026“. Beginn = späteres Datum von Einschreibung und
 * Ausbildungsstart, Ende = Ausstellungsdatum. Liegt beides im selben Monat (oder läge der Beginn
 * nach dem Ende, etwa bei Testzertifikaten vor dem Start), steht nur ein Monat da.
 */
export function zeitraumText(eingeschriebenAm: string | null | undefined, ausgestelltAm: string): string {
  const start = new Date(`${MS_AUSBILDUNGSSTART}T00:00:00`);
  const eingeschrieben = eingeschriebenAm ? new Date(eingeschriebenAm) : null;
  const beginn = eingeschrieben && eingeschrieben > start ? eingeschrieben : start;
  const ende = new Date(ausgestelltAm);
  const monat = (d: Date) => format(d, "MMMM yyyy", { locale: de });
  if (beginn >= ende || monat(beginn) === monat(ende)) return `Zeitraum: ${monat(ende)}`;
  return `Zeitraum: ${monat(beginn)} bis ${monat(ende)}`;
}

export function dozentinnenText(): string {
  return `Dozentinnen: ${MS_DOZENTINNEN.join(", ")}`;
}
